import {
  buildRhythmAudioFrame,
  resetRhythmAudioFrameState,
  type RhythmAudioFrameInput,
} from "./audioFrameBuilder";

import { keyCodeToRhythmLabel } from "./aiHub/keyLabelMap";

import { rhythmMusicActive } from "./aiHub/rhythmAudio";

import { OFF_COLOR } from "./aiHub/rhythmEngineCore";

import type { CanonicalKey } from "./aiHub/types";

import { buildRhythmLayoutKeysFromMatrix } from "./rhythmLayoutKeys";

import {
  mapBuiltinRhythmFrame,
  resetBuiltinRhythmModeState,
} from "./builtinRhythmModes";

import type { RhythmActivityInput } from "./deriveRhythmAmplitude";
import {
  isRhythmFramePlaybackActive,
  isRhythmNoiseOnly,
  isRhythmPlaybackActive,
  resetRhythmPlaybackGate,
} from "./rhythmPlaybackGate";

import { normalizeRhythmMode } from "./rhythmModeOptions";

import type { RhythmMode } from "./audioVisualizerTypes";

export interface BuiltinRhythmKeyColor {
  R: number;

  G: number;

  B: number;

  isCustom: boolean;
}

export type BuiltinRhythmKeyColorMap = Record<string, BuiltinRhythmKeyColor>;

export type BuiltinRhythmMapInput = RhythmAudioFrameInput & {
  mode: RhythmMode | string;

  layout: unknown[] | null | undefined;

  highlightColorHex?: string;
};

const OFF: BuiltinRhythmKeyColor = { R: 0, G: 0, B: 0, isCustom: false };

function resetRhythmOnSilence(): void {
  resetRhythmAudioFrameState();
  resetBuiltinRhythmModeState();
  resetRhythmPlaybackGate();
}

function isRhythmSilenceInput(input: BuiltinRhythmMapInput): boolean {
  const activity: RhythmActivityInput = {
    frequencyData: input.frequencyData,
    currentAmplitude: input.currentAmplitude,
    rms: input.rms,
    rhythmSpectrum: input.rhythmSpectrum,
    timestamp: input.timestamp,
  };
  return isRhythmNoiseOnly(activity) || !isRhythmPlaybackActive(activity);
}

function hexToHighlightColor(hex: string): { R: number; G: number; B: number } {
  const h = hex.replace(/^#/, "");

  if (!/^[0-9A-Fa-f]{6}$/.test(h)) {
    return { R: 0, G: 0, B: 0 };
  }

  return {
    R: parseInt(h.slice(0, 2), 16),

    G: parseInt(h.slice(2, 4), 16),

    B: parseInt(h.slice(4, 6), 16),
  };
}

function buildCanonicalKeys(
  layout: unknown[] | null | undefined
): CanonicalKey[] {
  return buildRhythmLayoutKeysFromMatrix(layout).map((key) => {
    const isHuafenda = key.ledIndex >= 0;

    return {
      uuid: key.code,

      row: key.row,

      col: key.col,

      widthU: 1,

      label: keyCodeToRhythmLabel(key.code),

      keyCode: key.keyCode,

      defaultKeyCode: key.keyCode,

      anchor: isHuafenda
        ? {
            provider: "huafenda",

            keyIndex: key.ledIndex,

            value: key.keyCode,

            row: key.row,

            col: key.col,

            layer: 0,
          }
        : {
            provider: "xingshan",

            row: key.row,

            col: key.col,

            layer: 0,
          },
    };
  });
}

function isOffColor(color: { r: number; g: number; b: number }): boolean {
  if (color.r + color.g + color.b < 8) return true;
  return (
    color.r === OFF_COLOR.r &&
    color.g === OFF_COLOR.g &&
    color.b === OFF_COLOR.b
  );
}

function applyHighlight(
  color: { r: number; g: number; b: number },

  highlight?: { R: number; G: number; B: number }
): BuiltinRhythmKeyColor {
  if (isOffColor(color)) return { ...OFF };

  if (!highlight) {
    return {
      R: color.r,

      G: color.g,

      B: color.b,

      isCustom: true,
    };
  }

  const srcMax = Math.max(color.r, color.g, color.b, 1);

  const v = srcMax / 255;

  return {
    R: Math.round(highlight.R * v),

    G: Math.round(highlight.G * v),

    B: Math.round(highlight.B * v),

    isCustom: true,
  };
}

function buildOffMap(
  layout: unknown[] | null | undefined
): BuiltinRhythmKeyColorMap {
  const keys = buildRhythmLayoutKeysFromMatrix(layout);

  const map: BuiltinRhythmKeyColorMap = {};

  for (const key of keys) {
    map[key.code] = { ...OFF };
  }

  return map;
}

/**

 * 内置 8 种律动模式统一入口：

 * 布局键位 → buildRhythmAudioFrame → 专用模式引擎 → 键色

 */

export function mapBuiltinRhythmToKeyColors(
  input: BuiltinRhythmMapInput
): BuiltinRhythmKeyColorMap {
  const {
    frequencyData,

    currentAmplitude,

    layout,

    highlightColorHex,

    mode,

    ...rhythmExtras
  } = input;

  const keys = buildCanonicalKeys(layout);

  if (isRhythmSilenceInput(input)) {
    resetRhythmOnSilence();
    return buildOffMap(layout);
  }

  const frame = buildRhythmAudioFrame({
    frequencyData,
    currentAmplitude,
    ...rhythmExtras,
  });

  if (!rhythmMusicActive(frame)) {
    resetRhythmOnSilence();
    return buildOffMap(layout);
  }

  if (!isRhythmFramePlaybackActive(frame, frequencyData)) {
    resetRhythmOnSilence();
    return buildOffMap(layout);
  }

  const normalizedMode = normalizeRhythmMode(mode);

  const mapping = mapBuiltinRhythmFrame(frame, keys, normalizedMode);

  const highlight = highlightColorHex
    ? hexToHighlightColor(highlightColorHex)
    : undefined;

  const result: BuiltinRhythmKeyColorMap = {};

  for (const key of keys) {
    const color = mapping.keyColors[key.uuid];

    result[key.uuid] = color ? applyHighlight(color, highlight) : { ...OFF };
  }

  return result;
}
